import React, { useEffect, useMemo, useState } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import { AppButton } from "../ui";
import API from "../../services/API";
import ExperiencesAPI from "../../services/ExperiencesAPI";
import { useContent } from "../../context/ContentContext";
import "./HomeExperienceRail.scss";

const FEATURED_COLLECTION_ID = "6739a511-3981-4b13-9ba0-68524af90775";
const COLLECTION_ITEM_TYPE_EXPERIENCE = 1;
const DEFAULT_DESCRIPTION = "Preview this experience in Viewer.";

const getCollectionPayload = (responseData) => {
  return responseData?.data || responseData || {};
};

const getExperienceItems = (collectionPayload) => {
  const items = Array.isArray(collectionPayload?.items) ? collectionPayload.items : [];

  return items
    .filter((item) => Number(item?.type) === COLLECTION_ITEM_TYPE_EXPERIENCE && item?.id)
    .sort((a, b) => {
      const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
};

const getCardTitle = (item) =>
  item?.name || item?.title || item?.metadata?.name || "Untitled Experience";

const getCardDescription = (item) =>
  item?.description || item?.summary || item?.metadata?.description || DEFAULT_DESCRIPTION;

const getExperiencePayload = (responseData) => responseData?.data || responseData || {};

const HomeExperienceRail = () => {
  const { getThumbnail } = useContent();
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchFeaturedExperiences = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await API.get(`collections/${FEATURED_COLLECTION_ID}`, {
          requiresAuth: true,
        });

        const payload = getCollectionPayload(response?.data);
        const experienceItems = getExperienceItems(payload);

        const cardsWithThumbnails = await Promise.all(
          experienceItems.map(async (item) => {
            let experienceDetails = null;

            try {
              const experienceResponse = await ExperiencesAPI.get(`experiences/${item.id}`, {
                requiresAuth: true,
              });
              experienceDetails = getExperiencePayload(experienceResponse?.data);
            } catch (detailError) {
              experienceDetails = null;
            }

            const thumbnailCandidates = [
              experienceDetails?.systemId,
              experienceDetails?.metadata?.systemId,
              item?.thumbnailId,
              item?.imageId,
              item?.systemId,
              item?.id,
            ].filter(Boolean);

            let thumbnailUrl = "";
            for (const candidateId of thumbnailCandidates) {
              const candidateThumbnail = await getThumbnail(candidateId);
              if (candidateThumbnail) {
                thumbnailUrl = candidateThumbnail;
                break;
              }
            }

            return {
              id: item.id,
              // Keep copy sourced from the curated collection item metadata.
              title: getCardTitle(item),
              description: getCardDescription(item),
              thumbnailUrl,
              viewerUrl: `/viewer/${item.id}`,
            };
          })
        );

        if (!cancelled) {
          setCards(cardsWithThumbnails);
        }
      } catch (fetchError) {
        console.error("Error loading featured experiences:", fetchError);
        if (!cancelled) {
          setError("Unable to load featured experiences right now.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchFeaturedExperiences();

    return () => {
      cancelled = true;
    };
  }, [getThumbnail]);

  const hasCards = useMemo(() => cards.length > 0, [cards.length]);

  return (
    <section className="homeExperienceRail">
      <div className="homeExperienceRail__header">
        <h2 className="homeExperienceRail__title">10k Science Content Library</h2>
      </div>

      {isLoading && (
        <div className="homeExperienceRail__status">
          Loading featured experiences...
        </div>
      )}

      {!isLoading && error && (
        <div className="homeExperienceRail__status homeExperienceRail__status--error">
          {error}
        </div>
      )}

      {!isLoading && !error && !hasCards && (
        <div className="homeExperienceRail__status">
          No featured experiences available yet.
        </div>
      )}

      {!isLoading && !error && hasCards && (
        <div className="homeExperienceRail__grid">
          {cards.map((card) => (
            <Link
              key={card.id}
              className="homeExperienceCard"
              to={card.viewerUrl}
              aria-label={`Preview ${card.title}`}
            >
              <div className="homeExperienceCard__media">
                {card.thumbnailUrl ? (
                  <img
                    className="homeExperienceCard__image"
                    src={card.thumbnailUrl}
                    alt={`${card.title} thumbnail`}
                    loading="lazy"
                  />
                ) : (
                  <div className="homeExperienceCard__fallback">
                    <span>{card.title}</span>
                  </div>
                )}
              </div>

              <div className="homeExperienceCard__body">
                <h3 className="homeExperienceCard__title">{card.title}</h3>
                <p className="homeExperienceCard__description">{card.description}</p>
                <AppButton
                  component="span"
                  kind="primary"
                  startIcon={<VisibilityOutlinedIcon />}
                  className="homeExperienceCard__launch"
                >
                  Preview
                </AppButton>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default HomeExperienceRail;
